import { createContext, useContext } from "react";
import { studioBegriffe } from "./studioBegriffe";

/**
 * Oberflächensprache für beide Portale (PARITAET-8). Texte stehen als Paar Deutsch/Englisch an der Stelle, an der sie
 * gebraucht werden: t("Rechnungen", "Invoices"). Die Sprache wählt jede Person selbst (user_preferences.language);
 * ohne eigene Wahl gilt die Sprache der Agentur (bzw. im Creator-Portal die des Profils), sonst Deutsch.
 *
 * Zwei Wege, dieselbe Sprache:
 * • `t(de, en)` liest die Sprache, die der Rahmen des Portals beim Zeichnen setzt (wie setDisplayLocale). Damit
 *   funktionieren auch Modell-Funktionen, Standardwerte und Beschriftungslisten ohne React. Der Rahmen zeichnet den
 *   Inhalt bei einem Sprachwechsel neu (key = Sprache), sodass nichts in der alten Sprache stehen bleibt.
 * • `useLang()`/`useT()` lesen den React-Kontext, den derselbe Rahmen setzt (bisheriger Weg des Creator-Portals).
 *
 * Nicht übersetzt werden Belege, Verträge und Mails (eigene Dokumentsprache) und Nutzerdaten.
 */

export type Lang = "de" | "en";

/** Text in einer bestimmten Sprache: tr(lang, "Deine Rechnungen", "Your invoices"). */
export function tr(lang: Lang, de: string, en: string): string {
  return lang === "en" ? en : de;
}

export function asLang(v: unknown): Lang | null {
  return v === "de" || v === "en" ? v : null;
}

/** Erste bekannte Sprache aus eigener Wahl, Agentur/Profil … ; sonst Deutsch. */
export function pickLang(...candidates: unknown[]): Lang {
  for (const c of candidates) {
    const l = asLang(typeof c === "string" ? c.slice(0, 2).toLowerCase() : c);
    if (l) return l;
  }
  return "de";
}

/**
 * Weg zu einer eigenständigen Seite mit eigener Sprachwahl (Anmeldung, Passwort, Registrieren) in derselben Sprache:
 * Englisch hängt ?sprache=en an (SICHT-OEFFENTLICH-BETRIEB-17: „Forgot your password?“ führte auf die deutsche Seite).
 */
export function mitSprache(path: string, lang: Lang): string {
  return lang === "en" ? `${path}${path.includes("?") ? "&" : "?"}sprache=en` : path;
}

/**
 * Nur in der Einbettung (?embed=1, Screenshots der Website): ?sprache=en zeigt das Portal auf Englisch, unabhängig vom
 * Profil der Demo-Person. Außerhalb der Einbettung null, damit echte Konten nie über die Adresse umgeschaltet werden.
 */
export function embedLang(): Lang | null {
  if (typeof document === "undefined" || document.documentElement.dataset.embed !== "true") return null;
  return asLang(new URLSearchParams(location.search).get("sprache"));
}

let uiLang: Lang = "de";
/** Setzt der Rahmen des Portals beim Zeichnen (und Tests vor dem Rendern). */
export function setUiLang(lang: Lang): void {
  uiLang = lang;
}
export function currentLang(): Lang {
  return uiLang;
}
/**
 * Führt fn in einer festen Sprache aus, z. B. für Nachrichten an Creator und Texte in Belegen,
 * die sich nicht nach der Oberfläche der angemeldeten Person richten.
 */
export function inLang<T>(lang: Lang, fn: () => T): T {
  const vorher = uiLang;
  const format = dateFormatPref;
  uiLang = lang;
  // Texte für andere (Nachrichten, Belege) im Datumsformat ihrer Sprache, nicht im eigenen der angemeldeten Person
  dateFormatPref = null;
  try {
    return fn();
  } finally {
    uiLang = vorher;
    dateFormatPref = format;
  }
}

/**
 * Eigenes Datumsformat je Person (PARITAET-R3-7, user_preferences.date_format): leer = nach der Oberflächensprache
 * (24.09.2026 bzw. 24/09/2026), „system“ = wie das Gerät, „dmy“ = TT.MM.JJJJ, „ymd“ = JJJJ-MM-TT, „mdy“ = MM/TT/JJJJ.
 * Gilt nur für die Anzeige; Belege, Verträge und Mails behalten ihr eigenes Format. Der Rahmen des Portals setzt es beim Zeichnen.
 */
export type DateFormat = "system" | "dmy" | "ymd" | "mdy";
export const DATE_FORMATS: readonly DateFormat[] = ["system", "dmy", "ymd", "mdy"];
let dateFormatPref: DateFormat | null = null;
export function asDateFormat(v: unknown): DateFormat | null {
  return (DATE_FORMATS as readonly unknown[]).includes(v) ? (v as DateFormat) : null;
}
export function setDateFormat(format: unknown): void {
  dateFormatPref = asDateFormat(format);
}
export function currentDateFormat(): DateFormat | null {
  return dateFormatPref;
}
/** Kalendertag (Jahr, Monat, Tag als Ziffern) im eigenen Format bzw. nach der Sprache. */
export function formatDay(y: string, m: string, d: string, lang: Lang, format: DateFormat | null = dateFormatPref): string {
  switch (format) {
    case "dmy": return `${d}.${m}.${y}`;
    case "ymd": return `${y}-${m}-${d}`;
    case "mdy": return `${m}/${d}/${y}`;
    case "system":
      try {
        return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(Date.UTC(Number(y), Number(m) - 1, Number(d)));
      } catch {
        break;
      }
    default:
      break;
  }
  return lang === "en" ? `${d}/${m}/${y}` : `${d}.${m}.${y}`;
}
/** Kalendertag eines Zeitpunkts in einer Zeitzone als [Jahr, Monat, Tag]. */
export function dayParts(value: string | Date, timeZone: string): [string, string, string] {
  const iso = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(typeof value === "string" ? new Date(value) : value);
  const [y = "", m = "", d = ""] = iso.split("-");
  return [y, m, d];
}
/**
 * Beschriftungen je Schlüssel, die erst beim Lesen in der Oberflächensprache entstehen (für Tabellen auf Modulebene,
 * die sonst beim Laden des Moduls einmal auf Deutsch ausgewertet würden).
 */
export function lazyRecord<K extends string>(keys: readonly K[], label: (key: K) => string): Record<K, string> {
  const out = {} as Record<K, string>;
  for (const k of keys) Object.defineProperty(out, k, { get: () => label(k), enumerable: true });
  return out;
}
/** Name einer Dokumentsprache in der Oberflächensprache („Deutsch“ / „German“). */
export function sprachName(lang: string | null | undefined): string {
  return lang === "en" ? t("Englisch", "English") : t("Deutsch", "German");
}
/** Zweisprachig gepflegter Name (z. B. Vertragsart name_de/name_en): der englische, wenn die Oberfläche englisch ist und er gepflegt ist. */
export function nameIn(de: string, en: string | null | undefined): string {
  return uiLang === "en" && en?.trim() ? en : de;
}
/** Text in der Oberflächensprache der angemeldeten Person. Im Studio (Creator-Konto) mit dessen Begriffen (Deal statt Kooperation). */
export function t(de: string, en: string): string {
  const s = uiLang === "en" ? en : schweizerSchreibung ? schweizerisch(de) : de;
  return creatorKonto ? studioBegriffe(s, uiLang) : s;
}

/**
 * K-284/K-305/K-394e: Agenturen in der Schweiz und in Liechtenstein (Zahlenformat de-CH/de-LI) lesen die Oberfläche in
 * Schweizer Schreibung: „ss“ statt „ß“, „Schadenersatz“ statt „Schadensersatz“. Der Rahmen setzt den Schalter beim Zeichnen
 * (wie die Sprache); Nutzerdaten laufen nicht durch t() und bleiben unverändert.
 */
let schweizerSchreibung = false;
export function setSchweizerSchreibung(locale: string | null | undefined): void {
  schweizerSchreibung = /^de-(CH|LI)$/i.test(String(locale ?? ""));
}
export function schweizerisch(text: string): string {
  return text.replace(/Schadensersatz/g, "Schadenersatz").replace(/ß/g, "ss");
}

/**
 * Creator-Konto (0178): Texte, die von „der Agentur“ sprechen, haben dort eine eigene Fassung; Kooperationen heißen dort
 * Deals (studioBegriffe, Runde 2 der Studio-Texte). Der Rahmen des Agentur-Portals
 * setzt den Schalter beim Zeichnen (wie die Sprache); außerhalb gilt die Agentur-Fassung.
 */
let creatorKonto = false;
export function setCreatorKonto(on: boolean): void {
  creatorKonto = on;
}
export function istCreatorKonto(): boolean {
  return creatorKonto;
}
/** Text, der nicht über t() läuft (z. B. aus dem Domain-Paket), im Studio mit dessen Begriffen (Deal statt Kooperation). */
export function studioText(text: string): string {
  return creatorKonto ? studioBegriffe(text, uiLang) : text;
}
/** ta("… der Agentur …", "… the agency …", "… deines Kontos …", "… your account …") */
export function ta(de: string, en: string, deCreator: string, enCreator: string): string {
  return creatorKonto ? t(deCreator, enCreator) : t(de, en);
}
/** Zahlen- und Datumsformat der Oberflächensprache (Beträge bleiben in ihrer Währung). */
export function uiLocale(lang: Lang = uiLang): "de-DE" | "en-GB" {
  return lang === "en" ? "en-GB" : "de-DE";
}

export const LangContext = createContext<Lang>("de");
export function useLang(): Lang {
  return useContext(LangContext);
}
export function useT(): (de: string, en: string) => string {
  const lang = useLang();
  return (de, en) => {
    const s = lang === "en" ? en : schweizerSchreibung ? schweizerisch(de) : de;
    return creatorKonto ? studioBegriffe(s, lang) : s;
  };
}

const MONTHS: Record<Lang, string[]> = {
  de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};
const MONTHS_SHORT: Record<Lang, string[]> = {
  de: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};
const WEEKDAYS: Record<Lang, string[]> = {
  de: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
};

/** '2026-09' oder '2026-09-01' → „September 2026“ */
export function monthOf(key: string, lang: Lang = "de", withYear = true): string {
  const [y, m] = key.split("-");
  const name = MONTHS[lang][Number(m) - 1] ?? key;
  return withYear ? `${name} ${y}` : name;
}
export function monthShortOf(key: string, lang: Lang = "de"): string {
  return MONTHS_SHORT[lang][Number(key.split("-")[1]) - 1] ?? key;
}
/** Monatsnamen 1–12 bzw. Wochentage (0 = Sonntag) in einer Sprache */
export function monthNames(lang: Lang = uiLang, short = false): string[] {
  return (short ? MONTHS_SHORT : MONTHS)[lang];
}
export function weekdayNames(lang: Lang = uiLang): string[] {
  return WEEKDAYS[lang];
}

/** Datum: 25.09.2026 bzw. 25/09/2026 (oder im eigenen Format der Person); Zeitpunkte in der angegebenen Zone. */
export function dateOf(value: string | null | undefined, lang: Lang = "de", timeZone = "Europe/Berlin"): string {
  if (!value) return "–";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y = "", m = "", d = ""] = value.split("-");
    return formatDay(y, m, d, lang);
  }
  if (Number.isNaN(new Date(value).getTime())) return value;
  return formatDay(...dayParts(value, timeZone), lang);
}

/** Zahl mit Komma bzw. Punkt („30,5“ / „30.5“) */
export function numOf(v: number, lang: Lang = "de"): string {
  return lang === "en" ? String(v) : String(v).replace(".", ",");
}

/**
 * Prozentangabe, die nicht umbricht (SICHT-CREATOR-19): Deutsch mit geschütztem Leerzeichen („30 %“), Englisch ohne
 * Leerzeichen („30%“). Vorher brach „(30 %)“ am Handy zwischen Zahl und Zeichen um.
 */
export function pctOf(v: number, lang: Lang = "de"): string {
  return lang === "en" ? `${numOf(v, "en")}%` : `${numOf(v, "de")} %`;
}

/** „1 Rechnung“ / „3 Rechnungen“ in der Oberflächensprache */
export function count(n: number, one: [de: string, en: string], many: [de: string, en: string]): string {
  const [de, en] = n === 1 ? one : many;
  return `${n.toLocaleString(uiLocale())} ${t(de, en)}`;
}
